/**
 * OAuth Callback Handler (SWRM-115)
 *
 * Handles the OAuth callback from Microsoft Azure AD.
 * Extracts code and state from URL, sends to backend,
 * and redirects to dashboard on success.
 */

import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuthStore } from "../store";

export const AuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);

  useEffect(() => {
    const handleCallback = async () => {
      // Extract OAuth parameters from URL
      const code = searchParams.get("code");
      const state = searchParams.get("state");
      const error = searchParams.get("error");
      const errorDescription = searchParams.get("error_description");

      // Check for OAuth errors
      if (error) {
        setError(
          `Microsoft login failed: ${errorDescription || error}`
        );
        setLoading(false);
        return;
      }

      // Validate required parameters
      if (!code || !state) {
        setError("Invalid callback - missing parameters");
        setLoading(false);
        return;
      }

      try {
        // Send code and state to backend to complete OAuth flow
        const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
        const response = await fetch(
          `${apiUrl}/api/auth/callback?code=${encodeURIComponent(
            code
          )}&state=${encodeURIComponent(state)}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({
            detail: "Authentication failed",
          }));
          throw new Error(errorData.detail || "Authentication failed");
        }

        const data = await response.json();

        // Store JWT token and user info
        if (data.token && data.user) {
          login(data.token, data.user);
          // Redirect to dashboard
          navigate("/", { replace: true });
        } else {
          throw new Error("Invalid response from server");
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Authentication failed"
        );
        setLoading(false);
      }
    };

    handleCallback();
  }, [searchParams, login, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white">
            {loading ? "Completing sign in..." : "Authentication Error"}
          </h2>

          {loading && (
            <div className="mt-8 flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
            </div>
          )}

          {error && (
            <div className="mt-8">
              <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
                <div className="text-sm text-red-700 dark:text-red-400">
                  {error}
                </div>
              </div>

              <div className="mt-6">
                <button
                  onClick={() => navigate("/login")}
                  className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Back to Login
                </button>
              </div>
            </div>
          )}

          {loading && (
            <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
              Please wait while we complete your authentication...
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

